import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../lib/hooks/useAuth";

/* ────────────────────────────────────────────────────────────────────────────
   Local icons (kept self-contained for navbar)
   ──────────────────────────────────────────────────────────────────────────── */
const SvgChevronRight = (p: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" {...p}>
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const SvgAccount = (p: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" {...p}>
    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
    <path d="M4 20c0-4 4-6 8-6s8 2 8 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

function clsx(...xs: Array<string | false | null | undefined>) {
  return xs.filter(Boolean).join(" ");
}

const EDGE_REM = 2.5;
const EDGE_OFFSET = `${EDGE_REM}rem`;
const COMPACT_REM = 1.25;
const NAV_BTN_H_REM = 2.75;
const FIRST_BTN_WIDTH_CH = 16;

/* ────────────────────────────────────────────────────────────────────────────
   ProfileButton (avatar + menu)
   ──────────────────────────────────────────────────────────────────────────── */
function ProfileButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const label = user ? (user.role || "Profile") : "Log in";
  const portalHref = user
    ? user.role === "admin" ? "/admin"
    : user.role === "team"  ? "/team"
    :                         "/client"
    : "/login";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        /* Mobile: avatar-only (no glass pill/text). Desktop: glass pill with text. */
        className={clsx(
          "flex items-center rounded-full overflow-hidden",
          "h-10 md:h-[var(--nav-h)]",                 // avatar sized on mobile, pill height on desktop
          "gap-0 md:gap-2",                            // no gap on mobile (no text)
          "p-0 md:pr-3 md:py-1",                       // remove pill padding on mobile
          "border-0 md:border md:border-white/30",     // no border on mobile
          "bg-transparent md:bg-white/10",             // no glass on mobile
          "hover:bg-transparent md:hover:bg-white/20", // no hover glass on mobile
          "backdrop-blur-none md:backdrop-blur-md"
        )}
        aria-label={user ? "Profile menu" : "Log in"}
      >
        <div className="w-10 h-10 rounded-full bg-white/80 text-[#0a0f29] grid place-items-center overflow-hidden">
          {user?.profile_pic_url ? (
            <img src={user.profile_pic_url} alt="avatar" className="w-full h-full object-cover" />
          ) : (
            <SvgAccount className="text-xl" />
          )}
        </div>

        {/* Hide label & chevron on mobile */}
        <span className="hidden md:inline text-white text-sm md:text-base whitespace-nowrap leading-none">{label}</span>
        <SvgChevronRight className="hidden md:inline text-white text-xl rotate-90" />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-56 rounded-xl bg-[#0a0f29] border border-white/15 shadow-xl overflow-hidden">
          {!user ? (
            <>
              <a href="/login" className="block px-4 py-3 hover:bg-white/10 text-white">Log in</a>
              <a href="/login" className="block px-4 py-3 hover:bg-white/10 text-white">Sign up</a>
            </>
          ) : (
            <>
              <a href={portalHref} className="block px-4 py-3 hover:bg-white/10 text-white">Go to portal</a>
              <button className="w-full text-left px-4 py-3 hover:bg-white/10 text-white">Toggle theme</button>
              <a href="/logout" className="block px-4 py-3 hover:bg-white/10 text-white">Log out</a>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   NavBar (desktop + mobile menu, smooth anchor scroll)
   ──────────────────────────────────────────────────────────────────────────── */
export default function NavBar() {
  const [compact, setCompact] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  useEffect(() => {
    const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const threshold = (EDGE_REM - COMPACT_REM) * rootPx;
    const onScroll = () => setCompact(window.scrollY >= threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const findSection = (id: string): HTMLElement | null =>
    document.getElementById(id) ||
    (document.querySelector(`[data-section="${id}"]`) as HTMLElement | null);

  const jump = (id: string) => (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    const el = findSection(id);
    if (!el) { window.location.hash = `#${id}`; return; }

    const nav = document.querySelector('[data-nav-root]') as HTMLElement | null;
    const offset = nav ? nav.getBoundingClientRect().height : 0;

    const FUDGE = 0;
    const top = el.getBoundingClientRect().top + window.scrollY - offset + FUDGE;

    window.scrollTo({ top, behavior: "smooth" });
  };

  const glassClasses =
    "bg-white/10 border-white/30 backdrop-blur-md " +
    "shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_6px_20px_rgba(2,8,23,0.35)]";
  const glassStyle: React.CSSProperties = {
    backgroundImage: "linear-gradient(to bottom, rgba(255,255,255,0.18), rgba(255,255,255,0.06))",
    WebkitBackdropFilter: "blur(8px)",
    backdropFilter: "blur(8px)",
  };

  return (
    <nav
      data-nav-root
      className={clsx(
        "fixed top-0 left-0 right-0 z-50 transition-colors duration-300",
        /* When menu is open on mobile, force opaque bg to remove the "empty transparent strip" */
        mobileOpen
          ? "bg-[#0a0f29] backdrop-blur-xl border-b border-white/10"
          : (compact ? "bg-[#0a0f29]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent")
      )}
    >
      <div
        className="w-full flex items-center transition-[padding] duration-300"
        style={
          {
            paddingLeft: EDGE_OFFSET,
            paddingRight: EDGE_OFFSET,
            paddingTop: `${compact ? COMPACT_REM : EDGE_REM}rem`,
            paddingBottom: `${compact ? COMPACT_REM : EDGE_REM}rem`,
            ["--nav-h" as any]: `${NAV_BTN_H_REM}rem`,
            ["--first-w" as any]: `${FIRST_BTN_WIDTH_CH}ch`,
          } as React.CSSProperties
        }
      >
        {/* Left: nav items (desktop) */}
        <ul className="hidden md:flex items-center gap-3">
          {[
            { id: "home", label: "Bracket & Brace", hover: "Home", emphasis: true },
            { id: "about", label: "About" },
            { id: "services", label: "Services" },
            { id: "portfolio", label: "Portfolio" },
            { id: "team", label: "Team" },
            { id: "blog", label: "Blog" },
            { id: "partners", label: "Partners" },
            { id: "faq", label: "FAQ" },
            { id: "contact", label: "Contact" },
          ].map((item) => {
            const isGlassy = hoveredId === item.id;
            const base =
              "group flex items-center justify-center h-[var(--nav-h)] px-4 box-border rounded-full " +
              "border leading-none transition-[background,box-shadow,border-color,color] duration-200 text-white";
            const normal = compact ? "border-white/30" : "border-transparent";
            const emphasis = item.emphasis ? "font-extrabold text-lg md:text-xl" : "text-base";

            return (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  onClick={jump(item.id)}
                  onMouseEnter={() => setHoveredId(item.id)}
                  onMouseLeave={() => setHoveredId((id) => (id === item.id ? null : id))}
                  onFocus={() => setHoveredId(item.id)}
                  onBlur={() => setHoveredId((id) => (id === item.id ? null : id))}
                  className={clsx(base, isGlassy ? glassClasses : normal, emphasis)}
                  style={isGlassy ? glassStyle : undefined}
                >
                  {item.hover ? (
                    <span className="relative inline-flex items-center justify-center w-[var(--first-w)] h-full">
                      <span className="absolute inset-0 flex items-center justify-center transition-opacity duration-200 ease-out opacity-100 group-hover:opacity-0">
                        Bracket &amp; Brace
                      </span>
                      <span className="absolute inset-0 flex items-center justify-center transition-opacity duration-200 ease-out opacity-0 group-hover:opacity-100">
                        Home
                      </span>
                    </span>
                  ) : (
                    <span className="text-center">{item.label}</span>
                  )}
                </a>
              </li>
            );
          })}
        </ul>

        {/* Mobile: burger + brand */}
        <div className="w-full flex items-center justify-between transition-[padding] duration-300 md:hidden">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-white"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
            <a
              href="#home"
              className="text-lg font-bold text-white md:text-lg"
              onClick={jump("home")}
            >
              Bracket &amp; Brace
            </a>
          </div>
        </div>

        {/* Right: profile */}
        <div className="ml-auto">
          <ProfileButton />
        </div>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <ul className="absolute top-full left-0 w-full bg-[#0a0f29] border-t border-white/10 flex flex-col items-center gap-3 p-4 md:hidden z-40">
          {[
            { id: "about", label: "About" },
            { id: "services", label: "Services" },
            { id: "portfolio", label: "Portfolio" },
            { id: "team", label: "Team" },
            { id: "blog", label: "Blog" },
            { id: "partners", label: "Partners" },
            { id: "faq", label: "FAQ" },
            { id: "contact", label: "Contact" },
          ].map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  jump(item.id)();
                  setMobileOpen(false);
                }}
                className="block w-full text-center text-white py-2 hover:bg-white/10 rounded-md transition"
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      )}
    </nav>
  );
}
